import { useCallback, useState } from 'react'
import type { StoreView } from '../types'

/**
 * Grid or list, remembered across visits.
 *
 * Same model as the Inventory page (`inventory:view`) and the Kill Race page (`killrace:view`):
 * `localStorage`, because choosing how to browse is a standing preference rather than a
 * per-session one, and a width-split default for the first visit.
 *
 * The split is at `md` (767px), which is where this grid's `minmax(220px,320px)` auto-fit stops
 * managing two columns. Below it the store is one ~370px tile per row, so a fifty-item collection
 * is a very long way down; the same fifty as 76px rows is about a quarter of that.
 */
const VIEW_KEY = 'store:view'

const initialView = (): StoreView => {
    try {
        const saved = localStorage.getItem(VIEW_KEY)
        if (saved === 'grid' || saved === 'list') return saved
    } catch {
        // Private-mode Safari throws on localStorage; the width default below is fine.
    }
    return window.matchMedia?.('(max-width: 767px)').matches ? 'list' : 'grid'
}

export const useStoreView = () => {
    const [view, setViewState] = useState<StoreView>(initialView)

    const setView = useCallback((next: StoreView) => {
        setViewState(next)
        try {
            localStorage.setItem(VIEW_KEY, next)
        } catch {}
    }, [])

    return { view, setView }
}
